/**
 * Provider Pair Configuration
 *
 * These provider pairs represent different versions (new/original) of the same game provider.
 * They must be activated/deactivated together to ensure proper game functionality.
 *
 * Format: [original_version, new_version]
 * - Original Version: Standard provider version from the game provider (cannot adjust win rate)
 * - New Version: Custom system version that allows win rate adjustment
 */

export type ProviderPair = [string, string]

/**
 * List of provider pairs that must be synchronized
 */
export const PROVIDER_PAIRS: ProviderPair[] = [
	['PG', 'PGHARD'],
	['JOKER', 'JOKERHARD'],
	['JILI', 'HARDV2_JILI'],
	['PP', 'HARDV2_PRAGMATIC'],
	['NLC', 'HARDV2_NOLIMITCITY'],
]

/**
 * Map provider name to its paired provider
 */
export const PROVIDER_PAIR_MAP = new Map<string, string>(
	PROVIDER_PAIRS.flatMap(([original, hard]) => [
		[original, hard],
		[hard, original],
	]),
)

/**
 * Get the paired provider name for a given provider
 * @param providerName - The provider name to find pair for
 * @returns The paired provider name, or undefined if no pair exists
 */
export function getPairedProvider(providerName: string): string | undefined {
	return PROVIDER_PAIR_MAP.get(providerName)
}

/**
 * Check if a provider is part of a pair
 * @param providerName - The provider name to check
 * @returns true if the provider is part of a pair
 */
export function isProviderPaired(providerName: string): boolean {
	return PROVIDER_PAIR_MAP.has(providerName)
}

/**
 * Get the display name for provider pairs
 * @param providerName - The provider name
 * @returns Formatted display name
 */
export function getProviderPairDisplayName(providerName: string): string {
	const pairedProvider = getPairedProvider(providerName)
	if (!pairedProvider) return providerName

	// Sort to always show in consistent order
	const [first, second] = [providerName, pairedProvider].sort()
	return `${first} ⇄ ${second}`
}
